import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { DynamoDBDocumentClient, QueryCommand, GetCommand, ScanCommand } from '@aws-sdk/lib-dynamodb';
import { authService } from './authService';
import { awsCredentialsService } from './awsCredentialsService';
import { getConfigValue } from './configService';
import { portalAdmin } from './portalAdmin';
import type { PortalConfig } from './configService';
import type { DeployBlock, DeploymentGroup } from '@/types';
import { clientMetadataService } from './clientMetadataService';
import { clientService } from './clientService';
import type { RepositoryName } from './imageTagService';
import { flagReader } from '@/utils/clientFlags';
import type { GroupSkippedClient } from '@/utils/groupRunSkips';

const AUTO_GROUP_NAME = 'All Production Clients';
const AUTO_GROUP_DESCRIPTION =
  'Kept up to date by itself: every active customer stack, plus three internal trial and demo stacks. A client marked inactive is left out.';
const AUTO_GROUP_DESCRIPTION_TAGS_UNREAD =
  'Kept up to date by itself: every customer stack, plus three internal trial and demo stacks. The inactive tags could not be read, so inactive clients are in this list too.';
const AUTO_GROUP_EXTRA_CLIENTS = new Set(['arcanum-prod-trial', 'arcanum-prod-numa-demo', 'arcanum-council-trial']);

/** The row that lists the clients a group run skipped. It is not a run, so no list of runs may show it. */
const SKIPPED_PREFIX = 'skipped#';

/**
 * A single deploy was refused because the client is marked do not deploy and
 * the person has not confirmed against the block as it stands now. The page
 * shows the block and asks for the client's name.
 */
export class DeployBlockedError extends Error {
  readonly clientName: string;
  readonly block: DeployBlock;
  constructor(clientName: string, block: DeployBlock) {
    super(`${clientName} is marked do not deploy. Read the reason, then type the client's name to deploy anyway.`);
    this.name = 'DeployBlockedError';
    this.clientName = clientName;
    this.block = block;
  }
}

/**
 * A group run was refused because the clients it skips (do not deploy,
 * inactive, not a client) are not the ones the person was shown. Nothing
 * started. `skipped` is the list as it stands now, for the page to show
 * before it asks again.
 */
export class BlockedClientsChangedError extends Error {
  readonly skipped: GroupSkippedClient[];
  constructor(skipped: GroupSkippedClient[]) {
    super('The clients this run skips changed while this was open. Nothing was started. Check the list again.');
    this.name = 'BlockedClientsChangedError';
    this.skipped = skipped;
  }
}

function getRegion(): string {
  return getConfigValue('AWS_REGION') || 'us-east-1';
}

export interface StartDeploymentInput {
  clientName: string;
  imageTag: string;
  repository: RepositoryName; // numa-deploy (prod) or numa-deploy-dev
  /** Not sent. The server records the person from the sign-in (TASK-638). */
  initiatedBy: string;
  deploymentLabel?: string; // optional custom label for deployment
  mode?: 'deploy' | 'plan';
  groupRunId?: string;
  groupName?: string;
  /**
   * For a client marked do not deploy: the `setAt` of the block the person
   * read before typing the client's name. A deploy to a blocked client is
   * refused without it, and refused when the block was set again since.
   */
  acknowledgedBlockSetAt?: string;
}

/**
 * Start one deploy or one plan, through the portal-admin Lambda (TASK-630).
 *
 * The server checks the client, the channel and the image (TASK-638), and
 * records the person from the sign-in. A deploy to a client marked do not
 * deploy starts only when the person has confirmed against the block as it
 * stands now (`acknowledgedBlockSetAt`); the server then records it in the
 * activity log. A plan changes nothing, so a block never stops one.
 */
export async function startDeployment(
  input: StartDeploymentInput
): Promise<{ executionArn: string; deploymentId: string }> {
  const res = await portalAdmin<
    { status: 'started'; executionArn: string; deploymentId: string } | { status: 'blocked'; block: DeployBlock }
  >('deployments.start', {
    clientName: input.clientName,
    imageTag: input.imageTag,
    repository: input.repository,
    mode: input.mode || 'deploy',
    ...(input.deploymentLabel && { deploymentLabel: input.deploymentLabel }),
    ...(input.groupRunId ? { groupRunId: input.groupRunId } : {}),
    ...(input.groupName ? { groupName: input.groupName } : {}),
    ...(input.acknowledgedBlockSetAt ? { acknowledgedBlockSetAt: input.acknowledgedBlockSetAt } : {}),
  });
  if (res.status === 'blocked') throw new DeployBlockedError(input.clientName, res.block);
  return { executionArn: res.executionArn, deploymentId: res.deploymentId };
}

/**
 * Start a group run, through the portal-admin Lambda (TASK-630).
 *
 * The server reads the tags and the client list at this moment, and leaves
 * three kinds of client out of the list it sends: a name with no client
 * config, a client marked do not deploy (a group has no override), and a
 * client marked inactive. When the clients to skip are not the ones the
 * person was shown (`shownSkipped`), nothing starts and
 * `BlockedClientsChangedError` carries the list as it now stands. After the
 * start, the server saves the skipped clients for the group page.
 */
export async function startGroupDeployment(input: StartGroupDeploymentInput): Promise<StartGroupDeploymentResult> {
  if (!input.clients || input.clients.length === 0)
    throw new Error('At least one client is required to start a group deployment');
  // The state machine reads this with MaxConcurrencyPath, which takes a whole
  // number. A group run's own record stores it as text, so a value copied from
  // a past run ("200") must be turned back into a number before it is sent.
  const parsedConcurrency = Math.floor(Number(input.maxConcurrency));
  const maxConcurrency = Number.isFinite(parsedConcurrency) && parsedConcurrency > 0 ? parsedConcurrency : undefined;

  const res = await portalAdmin<
    ({ status: 'started' } & StartGroupDeploymentResult) | { status: 'blocked-changed'; skipped: GroupSkippedClient[] }
  >('deployments.startGroup', {
    groupName: input.groupName,
    clients: input.clients,
    shownSkipped: input.shownSkipped,
    imageTag: input.imageTag,
    repository: input.repository,
    mode: input.mode || 'deploy',
    deploymentLabel: input.deploymentLabel ?? input.groupName,
    ...(maxConcurrency ? { maxConcurrency } : {}),
  });
  if (res.status === 'blocked-changed') throw new BlockedClientsChangedError(res.skipped);
  return {
    executionArn: res.executionArn,
    groupRunId: res.groupRunId,
    skipped: res.skipped,
    skippedSaved: res.skippedSaved,
  };
}

/** The clients one group run left out, and why. */
export interface SkippedClientsRecord {
  groupRunId: string;
  skipped: GroupSkippedClient[];
  createdAt?: string;
  createdBy?: string;
}

/** The clients a group run skipped, or null when it skipped none. One key read. */
export async function getSkippedClients(groupRunId: string): Promise<SkippedClientsRecord | null> {
  const table = getConfigValue('DEPLOYMENTS_TABLE');
  if (!table) throw new Error('Deployments table not configured');
  const res = await getDdbDoc().send(
    new GetCommand({ TableName: table, Key: { deploymentId: `${SKIPPED_PREFIX}${groupRunId}` } })
  );
  const item = res.Item as { skipped?: unknown; createdAt?: string; createdBy?: string } | undefined;
  if (!item || !Array.isArray(item.skipped)) return null;
  const skipped = item.skipped.filter(
    (entry): entry is GroupSkippedClient =>
      typeof entry === 'object' && entry !== null && typeof (entry as GroupSkippedClient).clientName === 'string'
  );
  return { groupRunId, skipped, createdAt: item.createdAt, createdBy: item.createdBy };
}

export interface DeploymentRecord {
  deploymentId: string;
  clientName: string;
  imageTag?: string;
  repository?: RepositoryName; // missing on legacy rows; treat as 'numa-deploy'
  initiatedBy?: string;
  status?: string;
  startedAt?: string;
  endedAt?: string;
  logsGroup?: string;
  logsStream?: string;
  ecsTaskArn?: string;
  sfnExecutionArn?: string;
  attemptNumber?: number;
  attemptLabel?: string;
  message?: string;
  // Written by the state machine's RecordFailure path for caught SFN errors
  // (message covers the exit/lock/timeout failure paths).
  error?: string;
  details?: string;
  deploymentLabel?: string;
  mode?: 'deploy' | 'plan';
  entityType?: 'deployment' | 'group';
  groupRunId?: string;
  groupName?: string;
  clientsJson?: string;
  clientsTotal?: number;
  clientsCompleted?: number;
  clientsSucceeded?: number;
  clientsFailed?: number;
  maxConcurrency?: number;
  lastFailedClient?: string;
  lastError?: string;
  lastActivityAt?: string;
  clients?: string[];
  // Why the run failed, written by the deploy pipeline when an attempt fails.
  // Older rows have none of these and the portal reads the reason from the log.
  errorSummary?: string;
  errorClass?: string;
  errorTitle?: string;
  errorAdvice?: string;
  /** Whether a second run fixes the error: `now`, `wait` or `never`. */
  errorRetry?: string;
  /** The attempt the error fields describe. 1 on a row at attempt 2 means the second attempt gave no reason. */
  errorAttempt?: number;
  // On a run the pipeline ran a second time: the first attempt's task and why it failed.
  // The first attempt's log stream is `ecs/deployer/<last segment of the task ARN>`.
  firstAttemptTaskArn?: string;
  firstAttemptErrorClass?: string;
  firstAttemptErrorSummary?: string;
  /** While the status is `retrying`: when the second attempt starts. */
  retryAt?: string;
  // Set when a person marks a failed run as dealt with. The run's own
  // endedAt and message are kept, so its duration and its error stay true.
  resolvedAt?: string;
  resolvedBy?: string;
  resolvedNote?: string;
}

export interface DeploymentLockRecord {
  deploymentId: string;
  clientName: string;
  startedAt?: string;
}

export interface StartGroupDeploymentInput {
  groupName: string;
  /** Every client the person asked for. The ones a group run skips are taken out before the start. */
  clients: string[];
  /**
   * The clients the confirm step told the person are skipped: not a client,
   * marked do not deploy, or marked inactive. The start is refused when the
   * table says otherwise.
   */
  shownSkipped: string[];
  imageTag: string;
  repository: RepositoryName;
  /** Not sent. The server records the person from the sign-in (TASK-638). */
  initiatedBy: string;
  mode?: 'deploy' | 'plan';
  maxConcurrency?: number;
  deploymentLabel?: string;
}

export interface StartGroupDeploymentResult {
  executionArn: string;
  groupRunId: string;
  /** The clients left out of the run, and why. */
  skipped: GroupSkippedClient[];
  /** False when the run started but its skipped clients could not be saved for the group page. */
  skippedSaved: boolean;
}

// The page polls live runs, so the client is kept: a new one for each call
// would fetch new AWS credentials for each call. A different person signed in
// gets a new one.
let cachedDdb: { doc: DynamoDBDocumentClient; owner: string } | null = null;

function getDdbDoc(): DynamoDBDocumentClient {
  const region = getRegion();
  const owner = `${authService.getCurrentUser()?.email || ''}|${region}`;
  if (!cachedDdb || cachedDdb.owner !== owner) {
    const client = awsCredentialsService.withRevokedRetry(
      new DynamoDBClient({ region, credentials: awsCredentialsService.credentials() })
    );
    cachedDdb = { doc: DynamoDBDocumentClient.from(client), owner };
  }
  return cachedDdb.doc;
}

function getGroupsTableName(): string {
  const table = getConfigValue('DEPLOYMENT_GROUPS_TABLE');
  if (!table) throw new Error('Deployment groups table not configured');
  return table;
}

function parseOptionalNumber(value: string | null): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

function getGroupDefaultConcurrencyConfig(): number | undefined {
  return parseOptionalNumber(getConfigValue('DEPLOYMENT_GROUP_DEFAULT_CONCURRENCY' as keyof PortalConfig));
}

function getGroupMaxConcurrencyConfig(): number | undefined {
  return parseOptionalNumber(getConfigValue('DEPLOYMENT_GROUP_MAX_CONCURRENCY' as keyof PortalConfig));
}

export function getGroupConcurrencyBounds(): { default?: number; max?: number } {
  return {
    default: getGroupDefaultConcurrencyConfig(),
    max: getGroupMaxConcurrencyConfig(),
  };
}

const NUMERIC_FIELDS = [
  'attemptNumber',
  'errorAttempt',
  'clientsTotal',
  'clientsCompleted',
  'clientsSucceeded',
  'clientsFailed',
  'maxConcurrency',
] as const;

function enrichDeploymentRecord(raw: DeploymentRecord | undefined | null): DeploymentRecord | null {
  if (!raw) return null;
  const record: DeploymentRecord = { ...raw };
  // A group run's record stores clientsTotal and maxConcurrency as text. Make
  // every count a number here, so no caller compares or sends a string.
  for (const field of NUMERIC_FIELDS) {
    const value = record[field] as unknown;
    if (typeof value === 'string') {
      const parsed = Number(value);
      record[field] = Number.isFinite(parsed) ? parsed : undefined;
    }
  }
  if (!record.clients && typeof record.clientsJson === 'string') {
    try {
      const parsed = JSON.parse(record.clientsJson);
      if (Array.isArray(parsed)) record.clients = parsed.filter((c): c is string => typeof c === 'string');
    } catch (error) {
      console.warn('Failed to parse clientsJson for deployment record', error);
    }
  }
  return record;
}

export async function getDeploymentById(deploymentId: string, consistent = true): Promise<DeploymentRecord | null> {
  const table = getConfigValue('DEPLOYMENTS_TABLE');
  if (!table) throw new Error('Deployments table not configured');
  const ddb = getDdbDoc();
  const res = await ddb.send(new GetCommand({ TableName: table, Key: { deploymentId }, ConsistentRead: consistent }));
  return enrichDeploymentRecord(res.Item as DeploymentRecord | undefined);
}

/**
 * Wait until a run that was just started can be read back. The record is
 * written a moment after the start call returns, and a list loaded before
 * then would not show the new run.
 */
export async function waitForDeploymentRecord(
  deploymentId: string,
  timeoutMs = 15_000
): Promise<DeploymentRecord | null> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const record = await getDeploymentById(deploymentId, true);
    if (record) return record;
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }
  return null;
}

export async function listRecentDeploymentsForClient(clientName: string, limit = 20): Promise<DeploymentRecord[]> {
  const table = getConfigValue('DEPLOYMENTS_TABLE');
  if (!table) throw new Error('Deployments table not configured');
  const ddb = getDdbDoc();
  const res = await ddb.send(
    new QueryCommand({
      TableName: table,
      IndexName: 'clientName-index',
      KeyConditionExpression: 'clientName = :c',
      ExpressionAttributeValues: { ':c': clientName },
      ScanIndexForward: false,
      Limit: limit,
    })
  );
  const items = (res.Items as DeploymentRecord[]) || [];
  return (
    items
      .map((item) => enrichDeploymentRecord(item))
      .filter((record): record is DeploymentRecord => Boolean(record))
      // The per-client lock row has the client's name too. It is not a run.
      .filter((record) => !record.deploymentId.startsWith('lock#'))
  );
}

/**
 * A client's newest run, or null. Used before offering to run an old failure
 * again: a newer run means the client moved on. It reads three rows, because
 * the newest row under a client's name can be its lock, which is not a run.
 */
export async function getLatestDeploymentForClient(clientName: string): Promise<DeploymentRecord | null> {
  const [latest] = await listRecentDeploymentsForClient(clientName, 3);
  return latest ?? null;
}

export async function listAllRecentDeployments(limit = 100): Promise<DeploymentRecord[]> {
  const table = getConfigValue('DEPLOYMENTS_TABLE');
  if (!table) throw new Error('Deployments table not configured');
  const ddb = getDdbDoc();

  // Paginated scan to reduce missed-late-page issues.
  // Note: Scan has no sort order; we fetch multiple pages and then sort by startedAt desc.
  const collected: DeploymentRecord[] = [];
  let exclusiveStartKey: Record<string, unknown> | undefined;
  let pages = 0;
  const maxPages = 5; // reasonable client-side bound; consider server/API or GSI for scale
  const pageSize = Math.max(100, Math.min(1000, limit * 2));

  do {
    const res = await ddb.send(
      new ScanCommand({
        TableName: table,
        Limit: pageSize,
        ExclusiveStartKey: exclusiveStartKey as any,
        // A lock and a run's list of skipped clients sit in this table too. Neither is a run.
        FilterExpression:
          'NOT begins_with(deploymentId, :lockPrefix) AND NOT begins_with(deploymentId, :skippedPrefix)',
        ExpressionAttributeValues: { ':lockPrefix': 'lock#', ':skippedPrefix': SKIPPED_PREFIX },
      })
    );
    const items = (res.Items as DeploymentRecord[]) || [];
    collected.push(...items);
    exclusiveStartKey = res.LastEvaluatedKey as any;
    pages += 1;
    // Stop early if we have significantly more than requested; we'll trim after sorting
  } while (exclusiveStartKey && pages < maxPages && collected.length < limit * 4);

  const normalized = collected
    .map((item) => enrichDeploymentRecord(item))
    .filter((record): record is DeploymentRecord => Boolean(record));

  normalized.sort((a, b) => {
    const timeA = a.startedAt ? new Date(a.startedAt).getTime() : 0;
    const timeB = b.startedAt ? new Date(b.startedAt).getTime() : 0;
    return timeB - timeA;
  });

  return normalized.slice(0, limit);
}

export interface ListByTimeRangeParams {
  fromIso: string;
  toIso?: string;
  pageSize?: number;
  exclusiveStartKey?: Record<string, unknown>;
  /**
   * When the range holds no row, look for rows written before the history
   * index existed. That is a table scan, so a caller on a timer passes false.
   */
  legacyFallback?: boolean;
}

export interface ListByTimeRangeResult {
  items: DeploymentRecord[];
  lastEvaluatedKey?: Record<string, unknown>;
}

export async function listDeploymentsByTimeRange(params: ListByTimeRangeParams): Promise<ListByTimeRangeResult> {
  const table = getConfigValue('DEPLOYMENTS_TABLE');
  if (!table) throw new Error('Deployments table not configured');
  const ddb = getDdbDoc();
  const toIso = params.toIso ?? new Date().toISOString();
  const pageSize = params.pageSize ?? 100;

  const res = await ddb.send(
    new QueryCommand({
      TableName: table,
      IndexName: 'history-index',
      KeyConditionExpression: 'historyPk = :h AND startedAt BETWEEN :from AND :to',
      ExpressionAttributeValues: {
        ':h': 'all',
        ':from': params.fromIso,
        ':to': toIso,
      },
      ScanIndexForward: false, // newest first
      Limit: pageSize,
      ExclusiveStartKey: params.exclusiveStartKey as any,
    })
  );

  let items = ((res.Items as DeploymentRecord[]) || [])
    .map((item) => enrichDeploymentRecord(item))
    .filter((record): record is DeploymentRecord => Boolean(record));

  // Temporary fallback for legacy items missing historyPk: scan a page from base table
  if (items.length === 0 && !res.LastEvaluatedKey && params.legacyFallback !== false) {
    const scan = await ddb.send(
      new ScanCommand({
        TableName: table,
        FilterExpression:
          'attribute_not_exists(historyPk) AND NOT begins_with(deploymentId, :lockPrefix) AND NOT begins_with(deploymentId, :skippedPrefix) AND #s BETWEEN :from AND :to',
        ExpressionAttributeNames: { '#s': 'startedAt' },
        ExpressionAttributeValues: {
          ':lockPrefix': 'lock#',
          ':skippedPrefix': SKIPPED_PREFIX,
          ':from': params.fromIso,
          ':to': toIso,
        },
        Limit: pageSize * 2,
      })
    );
    const legacy = ((scan.Items as DeploymentRecord[]) || [])
      .map((item) => enrichDeploymentRecord(item))
      .filter((record): record is DeploymentRecord => Boolean(record))
      .sort((a, b) => {
        const timeA = a.startedAt ? Date.parse(a.startedAt) : 0;
        const timeB = b.startedAt ? Date.parse(b.startedAt) : 0;
        return timeB - timeA;
      })
      .slice(0, pageSize);
    items = legacy;
  }

  return { items, lastEvaluatedKey: res.LastEvaluatedKey as any };
}

/**
 * Every run that is live now (running, or waiting for its second attempt),
 * group runs and their members included, newest first.
 *
 * The history shows a period, and a run still marked live from before that
 * period would be off the list: a dead run nobody marked failed is exactly
 * that. This read finds them. It reads the last 90 days of the history index
 * with a status filter, about 1.7 MB, so it runs when the list is loaded or
 * refreshed by a person, never on a timer.
 */
export async function listLiveDeployments(lookbackDays = 90): Promise<DeploymentRecord[]> {
  const table = getConfigValue('DEPLOYMENTS_TABLE');
  if (!table) throw new Error('Deployments table not configured');
  const ddb = getDdbDoc();
  const live: DeploymentRecord[] = [];
  let exclusiveStartKey: Record<string, unknown> | undefined;
  let pages = 0;
  do {
    const res = await ddb.send(
      new QueryCommand({
        TableName: table,
        IndexName: 'history-index',
        KeyConditionExpression: 'historyPk = :h AND startedAt >= :from',
        FilterExpression: '#s IN (:running, :retrying)',
        ExpressionAttributeNames: { '#s': 'status' },
        ExpressionAttributeValues: {
          ':h': 'all',
          ':from': new Date(Date.now() - lookbackDays * 24 * 60 * 60 * 1000).toISOString(),
          ':running': 'running',
          ':retrying': 'retrying',
        },
        ScanIndexForward: false,
        ExclusiveStartKey: exclusiveStartKey as any,
      })
    );
    for (const item of (res.Items as DeploymentRecord[]) || []) {
      const record = enrichDeploymentRecord(item);
      if (record) live.push(record);
    }
    exclusiveStartKey = res.LastEvaluatedKey as any;
    pages += 1;
  } while (exclusiveStartKey && pages < 12);
  return live;
}

/**
 * One client's runs in a time range, newest first, group members included. The
 * history filter uses this in place of filtering the shared list, because a
 * client's last deploy can sit far behind a fleet run's rows.
 */
export async function listDeploymentsForClient(
  params: ListByTimeRangeParams & { clientName: string }
): Promise<ListByTimeRangeResult> {
  const table = getConfigValue('DEPLOYMENTS_TABLE');
  if (!table) throw new Error('Deployments table not configured');
  const ddb = getDdbDoc();
  const res = await ddb.send(
    new QueryCommand({
      TableName: table,
      IndexName: 'clientName-index',
      KeyConditionExpression: 'clientName = :c AND startedAt BETWEEN :from AND :to',
      ExpressionAttributeValues: {
        ':c': params.clientName,
        ':from': params.fromIso,
        ':to': params.toIso ?? new Date().toISOString(),
      },
      ScanIndexForward: false,
      Limit: params.pageSize ?? 100,
      ExclusiveStartKey: params.exclusiveStartKey as any,
    })
  );
  const items = ((res.Items as DeploymentRecord[]) || [])
    .map((item) => enrichDeploymentRecord(item))
    .filter((record): record is DeploymentRecord => Boolean(record))
    // The per-client lock row has the client's name too. It is not a run.
    .filter((record) => !record.deploymentId.startsWith('lock#'));
  return { items, lastEvaluatedKey: res.LastEvaluatedKey as any };
}

/**
 * List recent multi-client group deployment *runs* (summary records) directly from the
 * history index. The Groups tab used to derive these by filtering the shared, time-paginated
 * `history` window, which hid any run whose summary sat beyond the first page — a large group
 * pushes its own summary past page 1 with its child rows. Query the index and filter for group
 * summaries, paginating until we have `limit` of them.
 */
export async function listRecentGroupRuns(limit = 25): Promise<DeploymentRecord[]> {
  const table = getConfigValue('DEPLOYMENTS_TABLE');
  if (!table) throw new Error('Deployments table not configured');
  const ddb = getDdbDoc();
  const runs: DeploymentRecord[] = [];
  let exclusiveStartKey: Record<string, unknown> | undefined;
  let pages = 0;
  const maxPages = 10; // bound cost; group runs are sparse relative to individual deploys
  do {
    const res = await ddb.send(
      new QueryCommand({
        TableName: table,
        IndexName: 'history-index',
        KeyConditionExpression: 'historyPk = :h',
        FilterExpression: '#et = :grp',
        ExpressionAttributeNames: { '#et': 'entityType' },
        ExpressionAttributeValues: { ':h': 'all', ':grp': 'group' },
        ScanIndexForward: false, // newest first
        ExclusiveStartKey: exclusiveStartKey as any,
      })
    );
    const items = ((res.Items as DeploymentRecord[]) || [])
      .map((item) => enrichDeploymentRecord(item))
      .filter((record): record is DeploymentRecord => Boolean(record));
    runs.push(...items);
    exclusiveStartKey = res.LastEvaluatedKey as any;
    pages += 1;
  } while (exclusiveStartKey && runs.length < limit && pages < maxPages);
  return runs.slice(0, limit);
}

export async function listDeploymentLocks(): Promise<DeploymentLockRecord[]> {
  const table = getConfigValue('DEPLOYMENTS_TABLE');
  if (!table) throw new Error('Deployments table not configured');
  const ddb = getDdbDoc();
  // Paginate the scan. DynamoDB reads <=1MB of items per page and applies the
  // FilterExpression *after* the read, so a single ScanCommand silently misses lock#
  // rows once the table grows past one page (they sit at arbitrary hash positions).
  // Without this loop a held/leaked lock can be invisible in the Locks tab even though
  // it exists and blocks deploys — so operators couldn't see it to release it.
  const items: DeploymentLockRecord[] = [];
  let exclusiveStartKey: Record<string, unknown> | undefined;
  do {
    const res = await ddb.send(
      new ScanCommand({
        TableName: table,
        FilterExpression: 'begins_with(deploymentId, :lockPrefix)',
        ExpressionAttributeValues: { ':lockPrefix': 'lock#' },
        ExclusiveStartKey: exclusiveStartKey as any,
      })
    );
    items.push(...((res.Items as DeploymentLockRecord[]) || []));
    exclusiveStartKey = res.LastEvaluatedKey as any;
  } while (exclusiveStartKey);
  return items;
}

export async function listDeploymentsForGroup(groupRunId: string): Promise<DeploymentRecord[]> {
  const table = getConfigValue('DEPLOYMENTS_TABLE');
  if (!table) throw new Error('Deployments table not configured');
  const ddb = getDdbDoc();
  // A fleet run is about 190 rows today and fits one page. The loop keeps the
  // list whole when rows grow (each failure now carries its error text).
  const items: DeploymentRecord[] = [];
  let exclusiveStartKey: Record<string, unknown> | undefined;
  do {
    const res = await ddb.send(
      new QueryCommand({
        TableName: table,
        IndexName: 'groupRunId-index',
        KeyConditionExpression: 'groupRunId = :g',
        ExpressionAttributeValues: { ':g': groupRunId },
        ScanIndexForward: true,
        ExclusiveStartKey: exclusiveStartKey as any,
      })
    );
    items.push(...((res.Items as DeploymentRecord[]) || []));
    exclusiveStartKey = res.LastEvaluatedKey as any;
  } while (exclusiveStartKey);

  return (
    items
      .map((item) => enrichDeploymentRecord(item))
      .filter((record): record is DeploymentRecord => Boolean(record))
      .filter((record) => record.entityType !== 'group' || record.deploymentId !== groupRunId)
      // The list of skipped clients carries no `groupRunId`, so this index never holds it. Belt and braces.
      .filter((record) => !record.deploymentId.startsWith(SKIPPED_PREFIX))
      .sort((a, b) => {
        const timeA = a.startedAt ? new Date(a.startedAt).getTime() : 0;
        const timeB = b.startedAt ? new Date(b.startedAt).getTime() : 0;
        return timeA - timeB;
      })
  );
}

/**
 * The lock one client holds, or null. One key read: the start form uses it to
 * warn that a deploy is already running, with no scan of the table.
 */
export async function getDeploymentLock(clientName: string): Promise<DeploymentLockRecord | null> {
  const table = getConfigValue('DEPLOYMENTS_TABLE');
  if (!table) throw new Error('Deployments table not configured');
  const ddb = getDdbDoc();
  const res = await ddb.send(
    new GetCommand({ TableName: table, Key: { deploymentId: `lock#${clientName}` }, ConsistentRead: true })
  );
  return (res.Item as DeploymentLockRecord | undefined) ?? null;
}

/** Release one client's deploy lock, through the portal-admin Lambda, so the next deploy can start. */
export async function releaseDeploymentLock(clientName: string): Promise<void> {
  await portalAdmin('deployments.releaseLock', { clientName });
}

export async function listDeploymentGroups(): Promise<DeploymentGroup[]> {
  const table = getGroupsTableName();
  const ddb = getDdbDoc();
  const res = await ddb.send(new ScanCommand({ TableName: table }));
  const items = (res.Items as DeploymentGroup[]) || [];

  const normalized = items.map((group) => ({
    ...group,
    clients: (group.clients || []).filter((client): client is string => typeof client === 'string'),
  }));

  let autoGroup: DeploymentGroup | null = null;
  try {
    const clients = await clientService.getAllClients();
    const eligibleClients = clients
      .filter((client) => !client.config.devInstance || AUTO_GROUP_EXTRA_CLIENTS.has(client.name))
      .map((client) => client.name)
      .sort((a, b) => a.localeCompare(b));

    // An inactive client is left out of the automatic group. When the tags cannot be read the
    // group still lists, with every client in it, and says that it could not leave any out.
    let isInactive: ((clientName: string) => boolean) | null = null;
    try {
      isInactive = flagReader(await clientMetadataService.loadAllMetadata({ fresh: true })).isInactive;
    } catch (error) {
      console.warn('Could not read the inactive tags for the automatic deployment group:', error);
    }
    const leftOut = isInactive ? eligibleClients.filter(isInactive) : undefined;
    const kept = isInactive ? eligibleClients.filter((name) => !isInactive(name)) : eligibleClients;

    if (kept.length > 0) {
      autoGroup = {
        groupName: AUTO_GROUP_NAME,
        clients: kept,
        description: isInactive ? AUTO_GROUP_DESCRIPTION : AUTO_GROUP_DESCRIPTION_TAGS_UNREAD,
        managed: true,
        inactiveLeftOut: leftOut,
      };
    }
  } catch (error) {
    console.warn('Failed to build automatic production deployment group:', error);
  }

  const merged = normalized.filter((group) => !autoGroup || group.groupName !== autoGroup.groupName);
  if (autoGroup) {
    merged.push(autoGroup);
  }

  return merged.sort((a, b) => {
    if (a.managed && !b.managed) return -1;
    if (!a.managed && b.managed) return 1;
    return a.groupName.localeCompare(b.groupName);
  });
}

export async function getDeploymentGroup(groupName: string): Promise<DeploymentGroup | null> {
  const table = getGroupsTableName();
  const ddb = getDdbDoc();
  const res = await ddb.send(new GetCommand({ TableName: table, Key: { groupName } }));
  if (!res.Item) return null;
  const group = res.Item as DeploymentGroup;
  return {
    ...group,
    clients: (group.clients || []).filter((client): client is string => typeof client === 'string'),
  };
}

function groupPayload(group: DeploymentGroup): Record<string, unknown> {
  return {
    groupName: group.groupName.trim(),
    clients: Array.from(new Set(group.clients.map((c) => c.trim()).filter(Boolean))).sort(),
    ...(group.description?.trim() ? { description: group.description.trim() } : {}),
    ...(group.maxConcurrency ? { maxConcurrency: group.maxConcurrency } : {}),
  };
}

/**
 * Save a deployment group, through the portal-admin Lambda (TASK-630). The
 * server keeps the date of the group's first save.
 */
export async function saveDeploymentGroup(group: DeploymentGroup): Promise<void> {
  if (!group.groupName.trim()) throw new Error('Group name is required');
  if (!group.clients || group.clients.length === 0) throw new Error('At least one client is required for a group');
  await portalAdmin('deployGroups.save', { group: groupPayload(group) });
}

/**
 * Rename a deployment group. The group name is the table's primary key, so the
 * server copies the group to the new name first and deletes the old name only
 * after that: a failed delete leaves a copy, never a lost group. The original
 * creation date is kept. Deployment history (keyed by groupRunId) keeps the old
 * name as its label, which is correct for audit.
 */
export async function renameDeploymentGroup(oldName: string, group: DeploymentGroup): Promise<void> {
  if (!group.groupName.trim()) throw new Error('Group name is required');
  await portalAdmin('deployGroups.rename', { oldName, group: groupPayload(group) });
}

export async function deleteDeploymentGroup(groupName: string): Promise<void> {
  await portalAdmin('deployGroups.delete', { groupName });
}

export function buildCloudwatchLogsUrl(region: string, group?: string, stream?: string): string | null {
  if (!group || !stream) return null;
  const enc = (s: string) => encodeURIComponent(s);
  return `https://console.aws.amazon.com/cloudwatch/home?region=${region}#logsV2:log-groups/log-group/${enc(group)}/log-events/${enc(stream)}`;
}

export function buildStepFunctionsUrl(region: string, executionArn?: string): string | null {
  if (!executionArn) return null;
  const enc = encodeURIComponent(executionArn);
  return `https://console.aws.amazon.com/states/home?region=${region}#/executions/details/${enc}`;
}

export function buildEcsTaskUrl(region: string, taskArn?: string): string | null {
  if (!taskArn) return null;
  const taskId = taskArn.split('/').pop() || taskArn;
  return `https://console.aws.amazon.com/ecs/home?region=${region}#/clusters/numa-portal-deployments/tasks/${encodeURIComponent(taskId)}/details`;
}

export async function getGroupDeploymentSummary(
  groupRunId: string,
  consistent = true
): Promise<DeploymentRecord | null> {
  return getDeploymentById(groupRunId, consistent);
}

/**
 * Mark a failed run as dealt with, through the portal-admin Lambda. The run
 * keeps its own end time and message, so the row still shows how long the
 * deploy ran and why it failed. The server records the person from the
 * sign-in, so `resolvedBy` is not sent.
 */
export async function markDeploymentResolved(deploymentId: string, _resolvedBy: string, note?: string): Promise<void> {
  await portalAdmin('deployments.markResolved', { deploymentId, ...(note?.trim() ? { note: note.trim() } : {}) });
}

/**
 * Mark a run failed or resolved. "Failed" also releases the client's deploy
 * lock, unless a newer run holds it: the server reads the client and the
 * run's start from the run's own row. The server writes the message and the
 * person, so `message` and `opts` are not sent.
 */
export async function overrideDeploymentStatus(
  deploymentId: string,
  status: 'failed' | 'resolved',
  message?: string,
  _opts?: { clientName?: string; recordStartedAt?: string; actor?: string }
): Promise<void> {
  // "Resolved" never rewrites the run's end time or message.
  if (status === 'resolved') {
    await markDeploymentResolved(deploymentId, '', message);
    return;
  }
  await portalAdmin('deployments.markFailed', { deploymentId });
}

/**
 * Stop a run, through the portal-admin Lambda. The server stops the execution
 * and the task named on the run's own row, releases the client's lock, and
 * marks the row stopped in the caller's name.
 */
export async function stopDeployment(deployment: DeploymentRecord, _stoppedBy: string): Promise<void> {
  await portalAdmin('deployments.stop', { deploymentId: deployment.deploymentId });
}

/** Stop a group run. The server stops its execution and marks its summary row stopped. */
export async function stopGroupDeployment(summary: DeploymentRecord, _stoppedBy: string): Promise<void> {
  await portalAdmin('deployments.stop', { deploymentId: summary.deploymentId });
}
